import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  Box, Button, Typography, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Link, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TableKit } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import MemberCard from '../members/MemberCard.jsx';
import ExamineesDashboard, {
  EXAMINEES_DASHBOARD_DEFAULT_FILTERS,
  EXAMINEES_FRAME_LABELS,
} from './ExamineesDashboard.jsx';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const POLL_MS = 5000;
const NEW_ROW_MS = 15000;

const ALLOWED_FRAMES = new Set(['daf_yomi', 'shinun', 'meshannim']);

const CHANNEL_LABEL = {
  web: { label: 'פורטל', icon: <LanguageIcon sx={{ fontSize: 16 }} />, color: 'var(--ck-primary, #1565c0)' },
  ivr: { label: 'טלפון', icon: <PhoneIcon sx={{ fontSize: 16 }} />, color: 'var(--ck-success, #2e7d32)' },
  import: { label: 'ייבוא', icon: <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />, color: 'var(--ck-text-2, #6d4c41)' },
};

// היסטוריה מיובאת — מאיפה הגיע הציון
const IMPORT_SOURCE_LABEL = { yemot: 'ימות', kehilot: 'קהילות' };
const channelText = (label, row) => (
  row?.importSource && IMPORT_SOURCE_LABEL[row.importSource]
    ? `${label} — ${IMPORT_SOURCE_LABEL[row.importSource]}`
    : label
);

const CHANNEL_FILTER_OPTIONS = [
  { value: 'web', label: 'פורטל' },
  { value: 'ivr', label: 'טלפון' },
  { value: 'import', label: 'ייבוא' },
];

const CHANNEL_QUERY = {
  web: 'web',
  ivr: 'ivr',
  import: 'import',
  פורטל: 'web',
  טלפון: 'ivr',
  ייבוא: 'import',
};

function memberDisplayName(row) {
  return [row.firstName, row.lastName].filter(Boolean).join(' ') || '—';
}

function resolveChannel(filters = {}, valueFilters = {}, dashFilters = {}) {
  const fromValues = Array.isArray(valueFilters.channel) && valueFilters.channel.length
    ? valueFilters.channel[0]
    : '';
  const raw = String(fromValues || filters.channel || dashFilters.channel || '').trim();
  const lower = raw.toLowerCase();
  return CHANNEL_QUERY[raw] || CHANNEL_QUERY[lower] || dashFilters.channel || '';
}

function buildQueryFromFilters(
  filters = {},
  dashFilters = {},
  valueFilters = {},
  { page, pageSize, sort, order } = {},
) {
  const search = [
    filters._name,
    filters.firstName,
    filters.lastName,
    filters.idNumber,
    filters.phone,
    filters.city,
    filters.occupation,
    filters.search,
  ].map((v) => String(v || '').trim()).find(Boolean) || '';

  const examId = String(dashFilters.examId || filters.examId || '').trim();
  const range = String(dashFilters.range || 'all').trim();
  const frame = String(dashFilters.frame || '').trim();

  return {
    ...(page != null ? { page, pageSize } : { all: 1 }),
    sort: sort || 'finishedAt',
    order: order || 'desc',
    channel: resolveChannel(filters, valueFilters, dashFilters),
    frame,
    examId,
    ...(range && range !== 'all' ? { range } : {}),
    search,
  };
}

const ExamineesList = () => {
  const { frame: frameParam } = useParams();
  const frame = String(frameParam || '').trim();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [flash, setFlash] = useState(null);
  const [newIds, setNewIds] = useState(() => new Set());
  const [dashFilters, setDashFilters] = useState(() => ({
    ...EXAMINEES_DASHBOARD_DEFAULT_FILTERS,
    frame,
  }));

  const fetchParamsRef = useRef({
    page: 1,
    pageSize: 20,
    sort: 'finishedAt',
    order: 'desc',
    filters: {},
    valueFilters: {},
  });
  const dashFiltersRef = useRef(dashFilters);
  const knownIdsRef = useRef(new Set());
  const initialLoadDoneRef = useRef(false);
  const newTimersRef = useRef(new Map());

  useEffect(() => {
    dashFiltersRef.current = dashFilters;
  }, [dashFilters]);

  useEffect(() => {
    if (!ALLOWED_FRAMES.has(frame)) return;
    setDashFilters((prev) => ({
      ...EXAMINEES_DASHBOARD_DEFAULT_FILTERS,
      frame,
      // שמירה על סינון ערוץ/מבחן לא רלוונטית במעבר מסגרת
      examId: '',
      channel: '',
      range: prev.range || 'all',
    }));
    knownIdsRef.current = new Set();
    initialLoadDoneRef.current = false;
    setNewIds(new Set());
  }, [frame]);

  const markNewRows = useCallback((rows) => {
    if (!initialLoadDoneRef.current) {
      knownIdsRef.current = new Set(rows.map((r) => String(r._id)));
      initialLoadDoneRef.current = true;
      return;
    }
    const fresh = [];
    for (const row of rows) {
      const id = String(row._id);
      if (!knownIdsRef.current.has(id)) {
        knownIdsRef.current.add(id);
        fresh.push(id);
      }
    }
    if (!fresh.length) return;

    setNewIds((prev) => {
      const next = new Set(prev);
      fresh.forEach((id) => next.add(id));
      return next;
    });

    for (const id of fresh) {
      const existing = newTimersRef.current.get(id);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        setNewIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        newTimersRef.current.delete(id);
      }, NEW_ROW_MS);
      newTimersRef.current.set(id, timer);
    }
  }, []);

  const fetchData = useCallback(async (params, { silent = false } = {}) => {
    const nextParams = {
      page: params?.page ?? fetchParamsRef.current.page,
      pageSize: params?.pageSize ?? fetchParamsRef.current.pageSize,
      sort: params?.sort || fetchParamsRef.current.sort || 'finishedAt',
      order: params?.order || fetchParamsRef.current.order || 'desc',
      filters: params?.filters ?? fetchParamsRef.current.filters ?? {},
      valueFilters: params?.valueFilters ?? fetchParamsRef.current.valueFilters ?? {},
    };
    fetchParamsRef.current = nextParams;

    if (!silent) setLoading(true);
    try {
      const query = buildQueryFromFilters(
        nextParams.filters,
        dashFiltersRef.current,
        nextParams.valueFilters,
        {
          page: nextParams.page,
          pageSize: nextParams.pageSize,
          sort: nextParams.sort,
          order: nextParams.order,
        },
      );
      const { data: res } = await api.get('/american-exams/attempts', { params: query });
      const rows = (res.data || []).map((r) => ({
        ...r,
        channel: CHANNEL_LABEL[r.channel]?.label || r.channel || '',
      }));
      setData(rows);
      setTotal(res.total || 0);
      markNewRows(rows);
    } catch (err) {
      console.error('שגיאה בשליפת נבחנים:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [markNewRows]);

  const handleExportFetch = useCallback(async ({ sort, order, filters, valueFilters }) => {
    const params = buildQueryFromFilters(
      filters,
      dashFiltersRef.current,
      valueFilters,
      { sort, order },
    );
    const { data: res } = await api.get('/american-exams/attempts', { params });
    return (res.data || []).map((r) => ({
      ...r,
      channel: CHANNEL_LABEL[r.channel]?.label || r.channel || '',
    }));
  }, []);

  const handleDashFiltersChange = useCallback((next) => {
    const locked = { ...next, frame };
    setDashFilters(locked);
    dashFiltersRef.current = locked;
  }, [frame]);

  const clearDashFilters = useCallback(() => {
    handleDashFiltersChange({
      ...EXAMINEES_DASHBOARD_DEFAULT_FILTERS,
      frame,
    });
  }, [frame, handleDashFiltersChange]);

  const confirmDeleteAttempt = useCallback(async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      await api.delete(`/american-exams/attempts/${deleteTarget._id}`);
      const deletedId = String(deleteTarget._id);
      setData((prev) => prev.filter((r) => String(r._id) !== deletedId));
      setTotal((prev) => Math.max(0, (prev || 0) - 1));
      knownIdsRef.current.delete(deletedId);
      setDeleteTarget(null);
      setFlash({ severity: 'success', message: 'ניסיון הנבחן נמחק — ניתן להיבחן שוב' });
      fetchData(fetchParamsRef.current, { silent: true });
    } catch (err) {
      setFlash({
        severity: 'error',
        message: err.response?.data?.error || 'שגיאה במחיקת הנבחן',
      });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, fetchData]);

  const fetchValuesFor = useCallback((colKey) => {
    if (colKey === 'channel') {
      return () => Promise.resolve(CHANNEL_FILTER_OPTIONS.map((o) => o.label));
    }
    return undefined;
  }, []);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      fetchData(fetchParamsRef.current, { silent: true });
    };
    const id = setInterval(tick, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      newTimersRef.current.forEach((t) => clearTimeout(t));
      newTimersRef.current.clear();
    };
  }, [fetchData]);

  const hasDashFilter = !!(dashFilters.examId || dashFilters.channel);
  const frameLabel = EXAMINEES_FRAME_LABELS[frame] || frame;

  const columns = useMemo(() => [
    {
      key: 'finishedAt',
      title: 'תאריך ושעה',
      sortable: true,
      minWidth: '140px',
      render: (v, row) => (
        <Box
          sx={{
            py: 0.25,
            px: newIds.has(String(row._id)) ? 1 : 0,
            borderRadius: 1,
            bgcolor: newIds.has(String(row._id)) ? 'rgba(46, 125, 50, 0.12)' : 'transparent',
            transition: 'background-color 0.4s',
          }}
        >
          {v ? formatHebrewDateTime(v) : '—'}
        </Box>
      ),
      exportRender: (v) => (v ? formatHebrewDateTime(v) : ''),
    },
    {
      key: '_name',
      title: 'שם',
      sortable: false,
      filterable: true,
      minWidth: '140px',
      render: (_, row) => {
        const name = memberDisplayName(row);
        if (!row.memberId) return name;
        return (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => setSelectedId(String(row.memberId))}
            sx={{
              fontWeight: 700,
              fontSize: 14,
              color: 'var(--ck-primary, #1565c0)',
              textAlign: 'inherit',
              cursor: 'pointer',
              border: 0,
              background: 'none',
              p: 0,
              fontFamily: 'inherit',
            }}
          >
            {name}
          </Link>
        );
      },
      exportRender: (_, row) => memberDisplayName(row),
    },
    {
      key: 'idNumber',
      title: 'ת.ז.',
      sortable: false,
      filterable: true,
      minWidth: '110px',
      render: (v) => v || '—',
    },
    {
      key: 'phone',
      title: 'טלפון',
      sortable: false,
      filterable: true,
      minWidth: '110px',
      render: (v) => v || '—',
    },
    {
      key: 'city',
      title: 'עיר',
      sortable: false,
      filterable: true,
      minWidth: '100px',
      render: (v) => v || '—',
    },
    {
      key: 'occupation',
      title: 'עיסוק',
      sortable: false,
      filterable: true,
      minWidth: '100px',
      render: (v) => v || '—',
    },
    {
      key: 'examTitle',
      title: 'מבחן',
      sortable: false,
      filterable: true,
      minWidth: '160px',
      render: (v) => v || '—',
    },
    {
      key: 'score',
      title: 'ציון',
      sortable: true,
      minWidth: '80px',
      render: (v, row) => (
        <Chip
          label={row.maxScore != null ? `${v ?? 0}/${row.maxScore}` : String(v ?? 0)}
          size="small"
          color={Number(v) >= 90 ? 'success' : 'default'}
          variant={Number(v) >= 90 ? 'filled' : 'outlined'}
          sx={{ fontWeight: 700 }}
        />
      ),
      exportRender: (v, row) => (row.maxScore != null ? `${v ?? 0}/${row.maxScore}` : String(v ?? 0)),
    },
    {
      key: 'correctCount',
      title: 'נכונות',
      sortable: false,
      minWidth: '80px',
      render: (v, row) => (row.totalQuestions ? `${v ?? 0}/${row.totalQuestions}` : '—'),
      exportRender: (v, row) => (row.totalQuestions ? `${v ?? 0}/${row.totalQuestions}` : ''),
    },
    {
      key: 'channel',
      title: 'ערוץ',
      sortable: false,
      filterable: true,
      minWidth: '100px',
      render: (v, row) => {
        const entry = CHANNEL_LABEL[v]
          || Object.values(CHANNEL_LABEL).find((e) => e.label === v)
          || CHANNEL_LABEL.web;
        return (
          <Chip
            icon={entry.icon}
            label={channelText(entry.label, row)}
            size="small"
            variant="outlined"
            sx={{ borderColor: entry.color, color: entry.color }}
          />
        );
      },
      exportRender: (v, row) => channelText(v || '', row),
    },
    {
      key: '_actions',
      title: 'פעולות',
      sortable: false,
      filterable: false,
      minWidth: '90px',
      exportRender: () => '',
      render: (_, row) => (
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => {
            e.stopPropagation();
            setDeleteTarget(row);
          }}
          sx={{ fontWeight: 600, minWidth: 0, px: 1 }}
        >
          מחיקה
        </Button>
      ),
    },
  ], [newIds]);

  if (!ALLOWED_FRAMES.has(frame)) {
    return <Navigate to="/examinees/daf_yomi" replace />;
  }

  return (
    <Box>
      <AdminPageHeader
        title={`נבחנים — ${frameLabel}`}
        sub="כל מי שסיים מבחן בפורטל או בקו הטלפוני — מתעדכן אוטומטית"
        legacy={(
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              נבחנים — {frameLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              כל מי שסיים מבחן בפורטל או בקו הטלפוני — מתעדכן אוטומטית
            </Typography>
          </>
        )}
      />

      <Box sx={{ mb: 2 }}>
        <ExamineesDashboard
          filters={dashFilters}
          onFiltersChange={handleDashFiltersChange}
          frame={frame}
        />
      </Box>

      {hasDashFilter && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button size="small" variant="contained" onClick={clearDashFilters}>
            הצג הכל
          </Button>
        </Box>
      )}

      <Dialog
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogContent sx={{ p: 3 }}>
          {selectedId ? (
            <MemberCard
              recordId={selectedId}
              onClose={() => setSelectedId(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, color: 'error.dark' }}>
          מחיקת נבחן
        </DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body1">
                למחוק את ניסיון הבחינה של{' '}
                <strong>«{memberDisplayName(deleteTarget)}»</strong>
                {' '}במבחן{' '}
                <strong>«{deleteTarget.examTitle || 'מבחן'}»</strong>
                {deleteTarget.maxScore != null
                  ? ` (ציון ${deleteTarget.score ?? 0}/${deleteTarget.maxScore})`
                  : ''}
                ?
              </Typography>
              <Alert severity="warning" variant="outlined">
                הפעולה תמחק את הרשומה לצמיתות ותאפשר לנבחן להיבחן שוב על אותו מבחן.
                לא ניתן לשחזור.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            ביטול
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteAttempt}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            מחק
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(flash)}
        autoHideDuration={4000}
        onClose={() => setFlash(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {flash ? (
          <Alert severity={flash.severity} onClose={() => setFlash(null)} variant="filled">
            {flash.message}
          </Alert>
        ) : null}
      </Snackbar>

      <TableKit
        key={`dash-${frame}-${dashFilters.range}-${dashFilters.channel}-${dashFilters.examId || 'all'}`}
        columns={columns}
        data={data}
        total={total}
        loading={loading}
        onFetch={fetchData}
        onExportFetch={handleExportFetch}
        fetchValuesFor={fetchValuesFor}
        clientSideMode={false}
        emptyMessage="אין נבחנים להצגה"
        exportFileName={`נבחנים-${frameLabel}`}
        onOpenCard={(row) => {
          if (row.memberId) setSelectedId(String(row.memberId));
        }}
      />
    </Box>
  );
};

export default ExamineesList;
